<script setup>
import { computed } from 'vue'

const props = defineProps({
    /**
     * The given segment.
     *
     * @type {Object}
     */
    shipment: {
        type: Object,
        required: true,
    },
})

/**
 * The credit hold.
 *
 * @return {String}
 */
const creditHold = computed(() => {
    const remaining = props.shipment?.purchase_order?.bill_to?.credit?.remaining

    return remaining ? `$ ${remaining}` : 'No'
})

/**
 * Determine if the segment has backhaul.
 *
 * @return {Boolean}
 */
const hasBackhaul = computed(() => {
    return props.shipment?.purchase_order?.bill_to?.handling_rules?.is_backhaul
})

/**
 * Get the hazmat class.
 *
 * @return {String}
 */
const hazmatClass = computed(() => {
    const hazmatClass = props.shipment?.handling_rules?.hazmat?.class

    return hazmatClass ? hazmatClass : 'Not Hazmat'
})

/**
 * Get the temperature controlled.
 *
 * @return {String}
 */
const temperatureControlled = computed(() => {
    const temperatureControlled =
        props.shipment?.handling_rules?.temperature_controlled

    if (temperatureControlled?.low && temperatureControlled?.high) {
        return `${temperatureControlled.low} - ${temperatureControlled.high}`
    }

    return 'No'
})
</script>

<template>
    <v-expansion-panel class="border !rounded-none mb-4" elevation="0">
        <v-expansion-panel-title hide-actions class="flex !px-2 !py-3">
            <template v-slot="slotProps">
                <div class="flex flex-col w-full justify-between">
                    <div class="flex">
                        <v-icon
                            size="sm"
                            class="border rounded border-gray-400 mr-3 h-6 w-6 text-gray-400"
                            :icon="
                                slotProps.expanded
                                    ? 'far fa-angle-up'
                                    : 'far fa-angle-down'
                            "
                        ></v-icon>

                        <div class="flex justify-between w-full">
                            <div class="flex flex-col">
                                <span class="text-base mb-2">
                                    PO
                                    {{
                                        shipment?.purchase_order?.name || 'N/A'
                                    }}
                                    &#183;
                                    {{
                                        shipment?.purchase_order?.bill_to?.name
                                    }}
                                    ({{
                                        shipment?.purchase_order?.bill_to?.code
                                    }})
                                </span>
                                <span class="text-xs text-gray-500">
                                    Shipment ID: {{ shipment?.name || 'N/A' }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </v-expansion-panel-title>
        <v-expansion-panel-text class="overflow-auto border-t">
            <div class="flex flex-col w-full p-6">
                <h4 class="font-medium mb-1">Billing Customer</h4>
                <div
                    class="flex flex-col w-full rounded border border-edge p-4 mb-4"
                >
                    <div class="flex justify-between w-full mb-2">
                        <div class="flex flex-col w-full mr-2">
                            <h5 class="text-xs text-gray-500 mb-2">Address</h5>
                            <span>
                                {{ shipment?.purchase_order?.bill_to?.address }}
                                {{
                                    shipment?.purchase_order?.bill_to?.address2
                                }}
                            </span>
                            <span
                                v-if="shipment?.purchase_order?.bill_to?.city"
                            >
                                {{ shipment?.purchase_order?.bill_to?.city }},
                                {{ shipment?.purchase_order?.bill_to?.state }}
                            </span>
                        </div>

                        <div class="flex flex-col w-full">
                            <h5 class="text-xs text-gray-500">Phone</h5>
                            <span>
                                {{
                                    shipment?.purchase_order?.bill_to?.phone ||
                                    'N/A'
                                }}
                            </span>
                        </div>

                        <div class="flex flex-col w-full">
                            <h5 class="text-xs text-gray-500">Credit hold</h5>
                            <span>
                                {{ creditHold }}
                            </span>
                        </div>
                    </div>
                </div>

                <h4 class="font-medium mb-1">Options</h4>
                <div
                    class="flex flex-col w-full mr-2 rounded-sm border border-edge p-4 mb-4"
                >
                    <div class="flex justify-between w-full mb-2">
                        <div class="flex flex-col w-full">
                            <span class="text-xs text-gray-500">
                                Backhaul
                            </span>
                            <span>
                                {{ hasBackhaul ? 'Yes' : 'No' }}
                            </span>
                        </div>
                        <div class="flex flex-col w-full">
                            <span class="text-xs text-gray-500"
                                >Hazmat class</span
                            >
                            <span>{{ hazmatClass }}</span>
                        </div>
                        <div class="flex flex-col w-full">
                            <span class="text-xs text-gray-500"
                                >Temperature controlled</span
                            >
                            <span>{{ temperatureControlled }}</span>
                        </div>
                    </div>
                </div>

                <h4 class="font-medium mb-1">Mileage</h4>
                <div class="flex flex-col w-full border border-edge p-4 mb-2">
                    <div class="flex flex-col w-full mb-4">
                        <span class="text-xs text-gray-500"> Mileage </span>
                        <span>
                            {{
                                (
                                    shipment.purchase_order?.billable_distance
                                        ?.loaded || 0
                                ).toFixed()
                            }}
                            loaded &#183;
                            {{
                                (
                                    shipment.purchase_order?.billable_distance
                                        ?.empty || 0
                                ).toFixed()
                            }}
                            empty
                        </span>
                    </div>
                </div>
            </div>
        </v-expansion-panel-text>
    </v-expansion-panel>
</template>
